import React, { useState, useEffect } from 'react';
import {
  FolderOpen,
  Plus,
  Search,
  Filter,
  Download,
  Calendar,
  FileText,
  Image as ImageIcon,
  Video,
  File,
  Clock,
  Eye,
  MessageSquare,
  GitBranch,
  CheckCircle,
  XCircle,
  AlertCircle,
  Archive,
  Grid,
  List,
  History,
  RotateCcw,
  Send,
  Users,
  Shield,
  Timer,
  Infinity
} from 'lucide-react';
import { contentRepositoryService } from '../../lib/contentRepositoryService';

export default function ContentRepository() {
  const [viewMode, setViewMode] = useState<'grid' | 'list'>('grid');
  const [selectedClient, setSelectedClient] = useState('f8a6a078-1684-4fc0-a7cb-70699d62105b');
  const [contentType, setContentType] = useState('all');
  const [status, setStatus] = useState('all');
  const [retentionFilter, setRetentionFilter] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [contentItems, setContentItems] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [showCreateModal, setShowCreateModal] = useState(false);
  const [selectedContent, setSelectedContent] = useState<any>(null);
  const [showVersionHistory, setShowVersionHistory] = useState(false);
  const [versions, setVersions] = useState<any[]>([]);
  const [showApprovalModal, setShowApprovalModal] = useState(false);
  const [showRetentionModal, setShowRetentionModal] = useState(false);
  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [downloadHistory, setDownloadHistory] = useState<any[]>([]);
  const [retentionPolicy, setRetentionPolicy] = useState<any>(null);

  const clients = [
    { id: 'f8a6a078-1684-4fc0-a7cb-70699d62105b', name: 'TechCorp' },
    { id: 'be540454-84ee-4c39-9d0f-d14039b03913', name: 'Fashion Brand' }
  ];

  useEffect(() => {
    loadContent();
    loadRetentionPolicy();
  }, [selectedClient, contentType, status, retentionFilter, searchQuery, startDate, endDate]);

  const loadContent = async () => {
    setLoading(true);

    let result;
    if (startDate && endDate) {
      result = await contentRepositoryService.getContentByDateRange(
        selectedClient,
        startDate,
        endDate,
        {
          content_type: contentType !== 'all' ? contentType : undefined,
          status: status !== 'all' ? status : undefined
        }
      );
    } else {
      result = await contentRepositoryService.getContentWithRetention(selectedClient);
    }

    if (result.success) {
      let filtered = result.data;

      if (retentionFilter !== 'all') {
        filtered = filtered.filter((c: any) => c.retention_status === retentionFilter);
      }

      if (searchQuery) {
        filtered = filtered.filter((c: any) =>
          c.title?.toLowerCase().includes(searchQuery.toLowerCase())
        );
      }

      if (contentType !== 'all') {
        filtered = filtered.filter((c: any) => c.content_type === contentType);
      }

      if (status !== 'all') {
        filtered = filtered.filter((c: any) => c.status === status);
      }

      setContentItems(filtered);
    }
    setLoading(false);
  };

  const loadRetentionPolicy = async () => {
    const result = await contentRepositoryService.getRetentionPolicy();
    if (result.success) {
      setRetentionPolicy(result.data);
    }
  };

  const loadVersionHistory = async (contentId: string) => {
    const result = await contentRepositoryService.getVersionHistory(contentId);
    if (result.success) {
      setVersions(result.data);
    }
  };

  const loadDownloadHistory = async () => {
    const result = await contentRepositoryService.getDownloadHistory(selectedClient, 50);
    if (result.success) {
      setDownloadHistory(result.data);
    }
  };

  const handleDownload = async (contentId: string, title: string) => {
    const result = await contentRepositoryService.downloadContent(
      contentId,
      selectedClient,
      undefined,
      'Current User',
      'pdf'
    );

    if (result.success && result.data.file_url) {
      window.open(result.data.file_url, '_blank');
      alert(`Download recorded! File: ${title}`);
      loadContent();
    } else {
      alert(result.error || 'Download failed');
    }
  };

  const handleCreateContent = async (e: React.FormEvent) => {
    e.preventDefault();
    const formData = new FormData(e.target as HTMLFormElement);

    const result = await contentRepositoryService.createContent({
      client_id: selectedClient,
      title: formData.get('title') as string,
      content_type: formData.get('content_type') as any,
      description: formData.get('description') as string,
      platform: formData.get('platform') as string,
      author_name: 'Current User',
      tags: (formData.get('tags') as string).split(',').map(t => t.trim()).filter(Boolean),
      status: 'draft'
    });

    if (result.success) {
      await contentRepositoryService.createVersion({
        content_id: result.data.id,
        title: formData.get('title') as string,
        content_data: { text: formData.get('content') as string },
        changed_by_name: 'Current User',
        is_major_version: true
      });

      setShowCreateModal(false);
      loadContent();
    }
  };

  const handleRequestApproval = async (contentId: string) => {
    await contentRepositoryService.requestApproval({
      content_id: contentId,
      requested_by_name: 'Current User',
      approver_name: 'Manager',
      comments: 'Ready for review'
    });
    setShowApprovalModal(false);
    loadContent();
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'draft': return 'bg-gray-100 text-gray-800';
      case 'pending_review': return 'bg-yellow-100 text-yellow-800';
      case 'approved': return 'bg-green-100 text-green-800';
      case 'published': return 'bg-blue-100 text-blue-800';
      case 'rejected': return 'bg-red-100 text-red-800';
      case 'archived': return 'bg-purple-100 text-purple-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRetentionColor = (retentionStatus: string) => {
    switch (retentionStatus) {
      case 'Active': return 'bg-green-100 text-green-800';
      case 'Expiring Soon': return 'bg-yellow-100 text-yellow-800';
      case 'Expired': return 'bg-red-100 text-red-800';
      case 'Keep Forever': return 'bg-blue-100 text-blue-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getRetentionIcon = (retentionStatus: string) => {
    switch (retentionStatus) {
      case 'Active': return <CheckCircle className="h-4 w-4" />;
      case 'Expiring Soon': return <AlertCircle className="h-4 w-4" />;
      case 'Expired': return <XCircle className="h-4 w-4" />;
      case 'Keep Forever': return <Infinity className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getStatusIcon = (status: string) => {
    switch (status) {
      case 'approved': return <CheckCircle className="h-4 w-4" />;
      case 'rejected': return <XCircle className="h-4 w-4" />;
      case 'pending_review': return <AlertCircle className="h-4 w-4" />;
      case 'archived': return <Archive className="h-4 w-4" />;
      default: return <Clock className="h-4 w-4" />;
    }
  };

  const getTypeIcon = (type: string) => {
    switch (type) {
      case 'post': return <FileText className="h-5 w-5" />;
      case 'image': return <ImageIcon className="h-5 w-5" />;
      case 'video': return <Video className="h-5 w-5" />;
      case 'article': return <FileText className="h-5 w-5" />;
      default: return <File className="h-5 w-5" />;
    }
  };

  const stats = [
    {
      label: 'Total Content',
      value: contentItems.length,
      icon: FolderOpen,
      color: 'blue'
    },
    {
      label: 'Expiring Soon',
      value: contentItems.filter(c => c.retention_status === 'Expiring Soon').length,
      icon: AlertCircle,
      color: 'yellow'
    },
    {
      label: 'Keep Forever',
      value: contentItems.filter(c => c.retention_status === 'Keep Forever').length,
      icon: Infinity,
      color: 'blue'
    },
    {
      label: 'Total Downloads',
      value: contentItems.reduce((sum, c) => sum + (c.total_downloads || 0), 0),
      icon: Download,
      color: 'green'
    },
  ];

  return (
    <div>
      {/* Header */}
      <div className="mb-6">
        <div className="flex items-center justify-between mb-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900">Content Repository</h1>
            <p className="text-gray-600 mt-1">Client-specific content with retention policies and download tracking</p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => {
                setShowRetentionModal(true);
                loadRetentionPolicy();
              }}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <Shield className="h-5 w-5 mr-2" />
              Retention Policy
            </button>
            <button
              onClick={() => {
                setShowDownloadModal(true);
                loadDownloadHistory();
              }}
              className="flex items-center px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
            >
              <History className="h-5 w-5 mr-2" />
              Download History
            </button>
            <button
              onClick={() => setShowCreateModal(true)}
              className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Plus className="h-5 w-5 mr-2" />
              New Content
            </button>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-6">
          {stats.map((stat) => (
            <div key={stat.label} className="bg-white p-4 rounded-lg border border-gray-200 hover:shadow-md transition-shadow">
              <div className="flex items-center justify-between">
                <div>
                  <p className="text-sm text-gray-600">{stat.label}</p>
                  <p className="text-2xl font-bold text-gray-900 mt-1">{stat.value}</p>
                </div>
                <div className={`p-3 bg-${stat.color}-100 rounded-lg`}>
                  <stat.icon className={`h-6 w-6 text-${stat.color}-600`} />
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* Filters */}
        <div className="bg-white p-4 rounded-lg border border-gray-200 space-y-4">
          {/* Client and Search Row */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Users className="h-5 w-5 text-gray-400" />
              <select
                value={selectedClient}
                onChange={(e) => setSelectedClient(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                {clients.map(client => (
                  <option key={client.id} value={client.id}>{client.name}</option>
                ))}
              </select>
            </div>

            <div className="flex-1 min-w-[300px]">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 h-5 w-5" />
                <input
                  type="text"
                  placeholder="Search content..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                />
              </div>
            </div>

            <div className="flex items-center gap-2">
              <button
                onClick={() => setViewMode('grid')}
                className={`p-2 rounded-lg ${viewMode === 'grid' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <Grid className="h-5 w-5" />
              </button>
              <button
                onClick={() => setViewMode('list')}
                className={`p-2 rounded-lg ${viewMode === 'list' ? 'bg-blue-100 text-blue-600' : 'text-gray-600 hover:bg-gray-100'}`}
              >
                <List className="h-5 w-5" />
              </button>
            </div>
          </div>

          {/* Date Range Row */}
          <div className="flex flex-wrap items-center gap-4">
            <div className="flex items-center gap-2">
              <Calendar className="h-5 w-5 text-gray-400" />
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="Start Date"
              />
              <span className="text-gray-500">to</span>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                placeholder="End Date"
              />
              {(startDate || endDate) && (
                <button
                  onClick={() => {
                    setStartDate('');
                    setEndDate('');
                  }}
                  className="text-sm text-blue-600 hover:text-blue-700"
                >
                  Clear
                </button>
              )}
            </div>
          </div>

          {/* Filter Row */}
          <div className="flex flex-wrap items-center gap-4">
            <select
              value={contentType}
              onChange={(e) => setContentType(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Types</option>
              <option value="post">Posts</option>
              <option value="article">Articles</option>
              <option value="image">Images</option>
              <option value="video">Videos</option>
              <option value="document">Documents</option>
              <option value="template">Templates</option>
            </select>

            <select
              value={status}
              onChange={(e) => setStatus(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Status</option>
              <option value="draft">Draft</option>
              <option value="pending_review">Pending Review</option>
              <option value="approved">Approved</option>
              <option value="published">Published</option>
              <option value="archived">Archived</option>
            </select>

            <select
              value={retentionFilter}
              onChange={(e) => setRetentionFilter(e.target.value)}
              className="px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
            >
              <option value="all">All Retention Status</option>
              <option value="Active">Active</option>
              <option value="Expiring Soon">Expiring Soon</option>
              <option value="Expired">Expired</option>
              <option value="Keep Forever">Keep Forever</option>
            </select>
          </div>
        </div>
      </div>

      {/* Content Grid/List */}
      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto"></div>
          <p className="mt-4 text-gray-600">Loading content...</p>
        </div>
      ) : contentItems.length === 0 ? (
        <div className="text-center py-12 bg-white rounded-lg border border-gray-200">
          <FolderOpen className="h-16 w-16 text-gray-400 mx-auto mb-4" />
          <h3 className="text-lg font-semibold text-gray-900 mb-2">No content found</h3>
          <p className="text-gray-600 mb-4">Create your first content item or adjust filters</p>
          <button
            onClick={() => setShowCreateModal(true)}
            className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
          >
            <Plus className="h-5 w-5 mr-2" />
            Create Content
          </button>
        </div>
      ) : viewMode === 'grid' ? (
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {contentItems.map((item) => (
            <div key={item.id} className="bg-white rounded-lg border border-gray-200 hover:shadow-lg transition-shadow">
              <div className="p-6">
                <div className="flex items-start justify-between mb-3">
                  <div className="flex items-center space-x-3">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      {getTypeIcon(item.content_type)}
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900 line-clamp-1">{item.title}</h3>
                      <p className="text-sm text-gray-500 capitalize">{item.content_type}</p>
                    </div>
                  </div>
                </div>

                <div className="flex flex-wrap gap-2 mb-3">
                  <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                    {getStatusIcon(item.status)}
                    {item.status?.replace('_', ' ')}
                  </span>
                  {item.retention_status && (
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRetentionColor(item.retention_status)}`}>
                      {getRetentionIcon(item.retention_status)}
                      {item.retention_status}
                    </span>
                  )}
                </div>

                {item.description && (
                  <p className="text-sm text-gray-600 mb-3 line-clamp-2">{item.description}</p>
                )}

                <div className="space-y-2 mb-4">
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Versions</span>
                    <span className="font-medium text-gray-900">{item.total_versions || 0}</span>
                  </div>
                  <div className="flex items-center justify-between text-sm">
                    <span className="text-gray-500">Downloads</span>
                    <span className="font-medium text-gray-900">{item.total_downloads || 0}</span>
                  </div>
                  {item.days_until_expiry !== null && item.days_until_expiry !== undefined && (
                    <div className="flex items-center justify-between text-sm">
                      <span className="text-gray-500">Days until expiry</span>
                      <span className={`font-medium ${item.days_until_expiry < 30 ? 'text-yellow-600' : 'text-gray-900'}`}>
                        {item.days_until_expiry}
                      </span>
                    </div>
                  )}
                  {item.retention_status === 'Keep Forever' && (
                    <div className="flex items-center justify-center text-sm text-blue-600 font-medium">
                      <Infinity className="h-4 w-4 mr-1" />
                      Permanent Retention
                    </div>
                  )}
                </div>

                <div className="flex items-center gap-2">
                  <button
                    onClick={() => {
                      setSelectedContent(item);
                      loadVersionHistory(item.id);
                      setShowVersionHistory(true);
                    }}
                    className="flex-1 px-3 py-2 text-sm border border-gray-300 rounded-lg hover:bg-gray-50"
                  >
                    <History className="h-4 w-4 inline mr-1" />
                    History
                  </button>
                  {item.is_downloadable && (
                    <button
                      onClick={() => handleDownload(item.id, item.title)}
                      className="flex-1 px-3 py-2 text-sm bg-green-600 text-white rounded-lg hover:bg-green-700"
                    >
                      <Download className="h-4 w-4 inline mr-1" />
                      Download
                    </button>
                  )}
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg border border-gray-200 overflow-x-auto">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Content</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Retention</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Days Left</th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase">Downloads</th>
                <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase">Actions</th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {contentItems.map((item) => (
                <tr key={item.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4">
                    <div className="flex items-center">
                      <div className="p-2 bg-blue-100 rounded-lg mr-3">
                        {getTypeIcon(item.content_type)}
                      </div>
                      <div>
                        <div className="font-medium text-gray-900">{item.title}</div>
                        {item.description && (
                          <div className="text-sm text-gray-500 line-clamp-1">{item.description}</div>
                        )}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900 capitalize">{item.content_type}</td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getStatusColor(item.status)}`}>
                      {getStatusIcon(item.status)}
                      {item.status?.replace('_', ' ')}
                    </span>
                  </td>
                  <td className="px-6 py-4">
                    <span className={`inline-flex items-center gap-1 px-2 py-1 rounded-full text-xs font-medium ${getRetentionColor(item.retention_status)}`}>
                      {getRetentionIcon(item.retention_status)}
                      {item.retention_status}
                    </span>
                  </td>
                  <td className="px-6 py-4 text-sm">
                    {item.days_until_expiry !== null && item.days_until_expiry !== undefined ? (
                      <span className={item.days_until_expiry < 30 ? 'text-yellow-600 font-medium' : 'text-gray-900'}>
                        {item.days_until_expiry}
                      </span>
                    ) : (
                      <span className="text-blue-600 flex items-center">
                        <Infinity className="h-4 w-4" />
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-sm text-gray-900">{item.total_downloads || 0}</td>
                  <td className="px-6 py-4 text-right text-sm space-x-2">
                    <button
                      onClick={() => {
                        setSelectedContent(item);
                        loadVersionHistory(item.id);
                        setShowVersionHistory(true);
                      }}
                      className="text-blue-600 hover:text-blue-700"
                    >
                      View
                    </button>
                    {item.is_downloadable && (
                      <button
                        onClick={() => handleDownload(item.id, item.title)}
                        className="text-green-600 hover:text-green-700"
                      >
                        Download
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {/* Retention Policy Modal */}
      {showRetentionModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Global Retention Policy</h2>
              <p className="text-sm text-gray-600 mt-1">Content retention periods by type and status</p>
            </div>
            <div className="p-6">
              {retentionPolicy ? (
                <div className="space-y-6">
                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">Default Retention</h3>
                    <div className="bg-gray-50 p-4 rounded-lg">
                      <p className="text-lg font-bold text-gray-900">{retentionPolicy.default_retention_days} days</p>
                      <p className="text-sm text-gray-600">Default retention period for all content</p>
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">By Content Type</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(retentionPolicy.by_content_type || {}).map(([type, days]: [string, any]) => (
                        <div key={type} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700 capitalize">{type}</p>
                          <p className="text-lg font-bold text-gray-900">
                            {days === -1 ? '∞ Forever' : `${days} days`}
                          </p>
                        </div>
                      ))}
                    </div>
                  </div>

                  <div>
                    <h3 className="font-semibold text-gray-900 mb-3">By Status</h3>
                    <div className="grid grid-cols-2 gap-3">
                      {Object.entries(retentionPolicy.by_status || {}).map(([status, days]: [string, any]) => (
                        <div key={status} className="bg-gray-50 p-3 rounded-lg">
                          <p className="text-sm font-medium text-gray-700 capitalize">{status}</p>
                          <p className="text-lg font-bold text-gray-900">{days} days</p>
                        </div>
                      ))}
                    </div>
                  </div>
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">Loading policy...</div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowRetentionModal(false)}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Download History Modal */}
      {showDownloadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Download History</h2>
              <p className="text-sm text-gray-600 mt-1">Complete audit trail of all downloads</p>
            </div>
            <div className="p-6">
              {downloadHistory.length > 0 ? (
                <div className="space-y-3">
                  {downloadHistory.map((dl) => (
                    <div key={dl.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                      <div className="flex items-start justify-between">
                        <div>
                          <p className="font-medium text-gray-900">{dl.user_name || 'Unknown User'}</p>
                          <p className="text-sm text-gray-600">{dl.download_type} • {dl.file_format || 'N/A'}</p>
                        </div>
                        <p className="text-sm text-gray-500">{new Date(dl.downloaded_at).toLocaleString()}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-center py-8 text-gray-500">No download history</div>
              )}
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowDownloadModal(false)}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Create Modal */}
      {showCreateModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Create New Content</h2>
            </div>
            <form onSubmit={handleCreateContent} className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Title</label>
                  <input
                    type="text"
                    name="title"
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Type</label>
                    <select
                      name="content_type"
                      required
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="post">Post</option>
                      <option value="article">Article</option>
                      <option value="image">Image</option>
                      <option value="video">Video</option>
                      <option value="document">Document</option>
                      <option value="template">Template</option>
                    </select>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">Platform</label>
                    <input
                      type="text"
                      name="platform"
                      placeholder="e.g. Instagram"
                      className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                    />
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Description</label>
                  <textarea
                    name="description"
                    rows={3}
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Content</label>
                  <textarea
                    name="content"
                    rows={6}
                    required
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">Tags (comma-separated)</label>
                  <input
                    type="text"
                    name="tags"
                    placeholder="campaign, summer, promo"
                    className="w-full px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
                  />
                </div>
              </div>

              <div className="flex justify-end gap-3 mt-6">
                <button
                  type="button"
                  onClick={() => setShowCreateModal(false)}
                  className="px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
                >
                  Cancel
                </button>
                <button
                  type="submit"
                  className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700"
                >
                  Create Content
                </button>
              </div>
            </form>
          </div>
        </div>
      )}

      {/* Version History Modal */}
      {showVersionHistory && selectedContent && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl max-w-4xl w-full max-h-[90vh] overflow-y-auto">
            <div className="p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">Version History: {selectedContent.title}</h2>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                {versions.map((version) => (
                  <div key={version.id} className="border border-gray-200 rounded-lg p-4">
                    <div className="flex items-start justify-between mb-2">
                      <div>
                        <h4 className="font-semibold text-gray-900">
                          Version {version.version_number}
                          {version.is_major_version && (
                            <span className="ml-2 px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded">Major</span>
                          )}
                        </h4>
                        <p className="text-sm text-gray-600">
                          by {version.changed_by_name} • {new Date(version.created_at).toLocaleString()}
                        </p>
                      </div>
                    </div>
                    {version.change_summary && (
                      <p className="text-sm text-gray-700 mb-2">
                        <strong>Changes:</strong> {version.change_summary}
                      </p>
                    )}
                    <button className="text-sm text-blue-600 hover:text-blue-700">
                      <RotateCcw className="h-4 w-4 inline mr-1" />
                      Restore this version
                    </button>
                  </div>
                ))}
              </div>
            </div>
            <div className="p-6 border-t border-gray-200 flex justify-end">
              <button
                onClick={() => setShowVersionHistory(false)}
                className="px-4 py-2 bg-gray-900 text-white rounded-lg hover:bg-gray-800"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
